import { useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import DashboardNavigation from './DashboardNavigation'
import Footer from './Footer'
import ClaimsFlowMain from './ClaimsFlowMain'
import { useSyncMfaPhone } from '../hooks/useSyncMfaPhone'
import { usePortalMfaGate } from '../hooks/usePortalMfaGate'
import { MfaCallCentreMobilePanel } from './MfaCallCentreMobilePanel'
import PostLoginLoadingScreen from './PostLoginLoadingScreen'

/**
 * Full-page screen when policy link succeeded but no AU mobile is on file for SMS MFA.
 * After a successful Auth0 SMS challenge, leave this page (MFA satisfied → dashboard;
 * phone enrolled but MFA still pending → `/mfa/verify`).
 */
export default function MfaMobileRequired() {
  const navigate = useNavigate()
  const { isSyncing, errorMessage, syncAndChallenge } = useSyncMfaPhone()
  const { loading, mfaSatisfied, smsMfaEnrolled, refresh } = usePortalMfaGate()

  useEffect(() => {
    if (loading) return
    if (mfaSatisfied) {
      navigate('/dashboard', { replace: true })
      return
    }
    if (smsMfaEnrolled) {
      navigate('/mfa/verify', { replace: true })
    }
  }, [loading, mfaSatisfied, smsMfaEnrolled, navigate])

  const handleUpdatedNumber = async () => {
    const result = await syncAndChallenge()
    if (result.challengeSucceeded) {
      await refresh()
      navigate('/dashboard', { replace: true })
      return
    }
    if (result.enrolled) {
      // Auth0 redirect may not resolve in-page; enrollment succeeded — verify next.
      navigate('/mfa/verify', { replace: true })
    }
  }

  if (loading || mfaSatisfied || smsMfaEnrolled) {
    return <PostLoginLoadingScreen />
  }

  return (
    <div className="relative flex min-h-screen w-full flex-col items-start bg-background-secondary">
      <DashboardNavigation />

      <ClaimsFlowMain>
        <Link
          to="/dashboard"
          className="font-body text-sm font-semibold text-foreground-brand-solid"
        >
          Back to dashboard
        </Link>

        <h1 className="m-0 font-display text-display-xs font-semibold leading-display-xs text-text-primary-900 md:text-display-sm md:leading-display-sm">
          Update your mobile number
        </h1>

        <MfaCallCentreMobilePanel
          reason="missing"
          errorMessage={errorMessage}
          isSyncing={isSyncing}
          onUpdatedNumber={() => {
            void handleUpdatedNumber()
          }}
        />
      </ClaimsFlowMain>

      <Footer />
    </div>
  )
}
